/**
 * Raga-fit evaluation (FR-5). Given a detected frequency, the chosen Sa, and the
 * raga's allowed swara set, decide whether the note is in-scale and by how many
 * cents it deviates from the nearest allowed swara's ideal JI pitch.
 */
import { nearestSwara, nearestSwaraAmong } from './pitch.js';
import { LEVEL_TOLERANCES } from './levels.js';
export const DEFAULT_TOLERANCE_CENTS = 25;
export function evaluateRagaFit(freqHz, saHz, allowedSwaras, toleranceCents = DEFAULT_TOLERANCE_CENTS) {
    const nearestOverall = nearestSwara(freqHz, saHz);
    const nearestAllowed = nearestSwaraAmong(freqHz, saHz, allowedSwaras);
    const inScale = nearestAllowed !== null && Math.abs(nearestAllowed.cents) <= toleranceCents;
    return { nearestOverall, nearestAllowed, inScale, toleranceCents };
}
/**
 * How far (cents) from a raga swara still counts as "in the raga". Set below the
 * tightest chromatic gap (~70¢, e.g. Ga3↔komal-ga) so a note sung a little flat/
 * sharp is still that raga note, not its foreign neighbour — but a deliberately
 * different swara sitting in a raga's gap still reads as out of the raga.
 */
export const IN_RAGA_BAND_CENTS = 65;
/**
 * Grade a pitch by raga membership first (forgiving), then centring. In-raga is
 * decided by distance to the nearest *allowed* swara (not which of all 12 is
 * closest), so a slightly flat Ga reads as Ga rather than komal-ga. `off` means
 * the pitch is more than IN_RAGA_BAND_CENTS from every raga swara — a genuine
 * wrong note or a note sitting in one of the raga's gaps.
 */
export function classifyPitch(freqHz, saHz, allowedSwaras, centeredToleranceCents) {
    const nearestOverall = nearestSwara(freqHz, saHz);
    const nearestAllowed = nearestSwaraAmong(freqHz, saHz, allowedSwaras);
    const centsFromAllowed = nearestAllowed ? nearestAllowed.cents : 0;
    const inRaga = nearestAllowed !== null && Math.abs(centsFromAllowed) <= IN_RAGA_BAND_CENTS;
    const status = !inRaga
        ? 'off'
        : Math.abs(centsFromAllowed) <= centeredToleranceCents
            ? 'on'
            : 'near';
    return { status, inRaga, nearestOverall, nearestAllowed, centsFromAllowed };
}
const fold = (c) => ((c % 1200) + 1200) % 1200;
/**
 * Grade a pitch against a raga's seed-driven target cents (from `ragaTargetCents`),
 * banded by practice level. Mirrors `classifyPitch`'s in-raga-first logic but adds a
 * finer excellent/good/off/wrong scale using `LEVEL_TOLERANCES[level].goodCents`.
 */
export function gradePitch(freqHz, saHz, targetCents, level) {
    const pc = fold(1200 * Math.log2(freqHz / saHz));
    let nearestSwara = '';
    let bestAbs = Infinity;
    let signed = 0;
    for (const [sym, tc] of Object.entries(targetCents)) {
        let d = pc - fold(tc);
        if (d > 600)
            d -= 1200;
        else if (d < -600)
            d += 1200;
        if (Math.abs(d) < bestAbs) {
            bestAbs = Math.abs(d);
            nearestSwara = sym;
            signed = d;
        }
    }
    const t = LEVEL_TOLERANCES[level];
    const inRaga = bestAbs <= IN_RAGA_BAND_CENTS;
    const band = !inRaga
        ? 'wrong'
        : bestAbs <= t.centeredCents
            ? 'excellent'
            : bestAbs <= t.goodCents
                ? 'good'
                : 'off';
    return { band, nearestSwara, centsFromNearest: signed, inRaga };
}
/**
 * Gamaka-tolerant smoothing: the median pitch (in cents relative to Sa) over
 * samples within ±halfWindowMs of `samples[index]`, returned as Hz. An
 * oscillation *around* a swara collapses to that swara's centre, so a kampita on
 * Ga reads as Ga rather than as a smear through its neighbours. `samples` must be
 * sorted by `t`. Returns the raw pitch when no window neighbours exist.
 */
export function smoothPitchHz(samples, index, saHz, halfWindowMs = 90) {
    const centre = samples[index];
    if (!centre)
        return saHz;
    const cents = [];
    for (let j = index; j >= 0 && centre.t - samples[j].t <= halfWindowMs; j--) {
        cents.push(1200 * Math.log2(samples[j].freqHz / saHz));
    }
    for (let j = index + 1; j < samples.length && samples[j].t - centre.t <= halfWindowMs; j++) {
        cents.push(1200 * Math.log2(samples[j].freqHz / saHz));
    }
    if (cents.length === 0)
        return centre.freqHz;
    cents.sort((a, b) => a - b);
    const med = cents[Math.floor(cents.length / 2)];
    return saHz * 2 ** (med / 1200);
}
/** Cents/second between two timed pitch samples; 0 when time does not advance. */
export function pitchVelocityCents(prevHz, prevSec, hz, sec) {
    const dt = sec - prevSec;
    if (dt <= 0)
        return 0;
    return (1200 * Math.log2(hz / prevHz)) / dt;
}
/** Above this pitch speed the singer is mid-gamaka/slide → treat as "transition". */
export const TRANSITION_VELOCITY_CPS = 600;
//# sourceMappingURL=evaluate.js.map